import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMatch } from '../context/MatchContext'

function cryptoRandomId() {
    return Math.random().toString(36).slice(2, 9)
}

function makeDefaultPlayers(count) {
    return Array.from({ length: count }, (_, i) => ({
        id: cryptoRandomId(),
        name: `Player ${i + 1}`,
        runs: 0,
        balls: 0,
        fours: 0,
        sixes: 0,
    }))
}

export default function NewMatch() {
    const navigate = useNavigate()
    const { startMatch } = useMatch()

    const [teamAName, setTeamAName] = useState('Team A')
    const [teamBName, setTeamBName] = useState('Team B')
    const [playersPerTeam, setPlayersPerTeam] = useState(6)
    const [oversLimit, setOversLimit] = useState(6)

    const [teamAPlayers, setTeamAPlayers] = useState(makeDefaultPlayers(6))
    const [teamBPlayers, setTeamBPlayers] = useState(makeDefaultPlayers(6))
    const [showTeamA, setShowTeamA] = useState(true)
    const [showTeamB, setShowTeamB] = useState(true)

    // update player name
    const updatePlayerName = (team, index, name) => {
        if (team === 'A') {
            const updated = [...teamAPlayers]
            updated[index].name = name
            setTeamAPlayers(updated)
        } else {
            const updated = [...teamBPlayers]
            updated[index].name = name
            setTeamBPlayers(updated)
        }
    }

    // adjust players when count changes
    const handlePlayerCountChange = (count) => {
        const newCount = Math.max(2, Math.min(11, Number(count)))
        setPlayersPerTeam(newCount)
        setTeamAPlayers(makeDefaultPlayers(newCount))
        setTeamBPlayers(makeDefaultPlayers(newCount))
    }

    const submit = (e) => {
        e.preventDefault()

        const teamA = { name: teamAName, players: teamAPlayers }
        const teamB = { name: teamBName, players: teamBPlayers }

        const match = {
            id: cryptoRandomId(),
            teamA,
            teamB,
            oversLimit: Number(oversLimit),
            inning: 1,
            batting: 'A',
            scoreA: { runs: 0, wickets: 0, balls: 0, extras: 0 },
            scoreB: { runs: 0, wickets: 0, balls: 0, extras: 0 },
            players: {
                A: teamA.players.map((p) => ({
                    ...p,
                    isOut: false,
                    onStrike: false,
                    overs: 0,
                    runsConceded: 0,
                    wicketsTaken: 0,
                })),
                B: teamB.players.map((p) => ({
                    ...p,
                    isOut: false,
                    onStrike: false,
                    overs: 0,
                    runsConceded: 0,
                    wicketsTaken: 0,
                })),
            },
            current: {
                strikerIndex: 0,
                nonStrikerIndex: 1,
                bowlerIndex: 0,
            },
            history: [],
            finished: false,
        }

        match.players.A[0].onStrike = true
        startMatch(match)
        navigate('/live')
    }

    return (
        <div className="space-y-4">
            <form
                onSubmit={submit}
                className="bg-gray-800 p-4 rounded-2xl shadow-md space-y-4"
            >
                <div>
                    <label className="text-sm text-gray-300">Team A</label>
                    <input
                        value={teamAName}
                        onChange={(e) => setTeamAName(e.target.value)}
                        className="w-full mt-1 p-2 rounded bg-gray-900"
                    />
                </div>
                <div>
                    <label className="text-sm text-gray-300">Team B</label>
                    <input
                        value={teamBName}
                        onChange={(e) => setTeamBName(e.target.value)}
                        className="w-full mt-1 p-2 rounded bg-gray-900"
                    />
                </div>

                <div className="flex gap-2">
                    <div className="flex-1">
                        <label className="text-sm text-gray-300">Players / team</label>
                        <input
                            type="number"
                            min="2"
                            max="11"
                            value={playersPerTeam}
                            onChange={(e) => handlePlayerCountChange(e.target.value)}
                            className="w-full mt-1 p-2 rounded bg-gray-900"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="text-sm text-gray-300">Overs</label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={oversLimit}
                            onChange={(e) => setOversLimit(e.target.value)}
                            className="w-full mt-1 p-2 rounded bg-gray-900"
                        />
                    </div>
                </div>

                {/* Team A Section */}
                <div className="mt-4 border-t border-gray-700 pt-3">
                    <button
                        type="button"
                        onClick={() => setShowTeamA(!showTeamA)}
                        className="w-full flex justify-between items-center text-accent text-sm font-semibold"
                    >
                        <span>{teamAName} Players</span>
                        <span>{showTeamA ? '▲' : '▼'}</span>
                    </button>
                    {showTeamA && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                            {teamAPlayers.map((p, i) => (
                                <input
                                    key={p.id}
                                    value={p.name}
                                    onChange={(e) => updatePlayerName('A', i, e.target.value)}
                                    className="p-2 rounded bg-gray-900 text-sm text-gray-200"
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Team B Section */}
                <div className="mt-2 border-t border-gray-700 pt-3">
                    <button
                        type="button"
                        onClick={() => setShowTeamB(!showTeamB)}
                        className="w-full flex justify-between items-center text-accent text-sm font-semibold"
                    >
                        <span>{teamBName} Players</span>
                        <span>{showTeamB ? '▲' : '▼'}</span>
                    </button>
                    {showTeamB && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                            {teamBPlayers.map((p, i) => (
                                <input
                                    key={p.id}
                                    value={p.name}
                                    onChange={(e) => updatePlayerName('B', i, e.target.value)}
                                    className="p-2 rounded bg-gray-900 text-sm text-gray-200"
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-2 pt-3">
                    <button
                        type="submit"
                        className="flex-1 py-2 rounded-lg bg-accent text-gray-900 font-semibold"
                    >
                        Start Match
                    </button>
                </div>
            </form>

            <section className="text-sm text-gray-400">
                Tip: Tap each team to expand or collapse their player list. You can
                rename players before starting the match.
            </section>
        </div>
    )
}
